import { Checklist, ChecklistCriteria } from "types/Checklist";
import { SubmittedDocument } from "types/SubmittedDocument";
import { CellTextParagraphs, WordChecklistCells } from "types/WordChecklist";
import { useCalculateNumParagraphColumns } from "~/composables/wordGenerator/useCalculateNumParagraphColumns";
import {
  DocumentGradedState,
  DocumentGradedStateLabels,
} from "~/types/enums/DocumentGradedState.enum";
import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";
import { Paragraph as RevisionParagraph } from "~/types/Paragraph";
import { usePDFChecklistCriteriaGenerator } from "./usePDFChecklistCriteriaGenerator";
import { usePDFChecklistSectionGenerator } from "./userPDFChecklistSectionGenerator";
import { useShouldIncludeChecklistCriteriaCheckmark } from "../useShouldIncludeChecklistCriteriaCheckmark";

const CHECKMARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path d="M2 6.5 L4.5 9 L10 3.5" stroke="#000000" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const useChecklistMarkCell = (includeCriteriaCheckmarks: boolean) => {
  if (!includeCriteriaCheckmarks) {
    return {
      text: "",
      alignment: "center",
    };
  }

  return {
    svg: CHECKMARK_SVG,
    fit: [10, 10],
    alignment: "center",
    margin: [0, 2, 0, 0],
  };
};

export const usePDFChecklistGenerator = (
  submittedDocument: SubmittedDocument,
  checklist: Checklist | undefined,
  wordChecklistCells: WordChecklistCells,
  allCriteria: ChecklistCriteria[],
  totalEarnedScore: number,
  totalScore: number,
  hideScores: boolean = false,
  includeCriteriaCheckmarks: boolean = false,
  isStudentMode: boolean = false
) => {
  if (!checklist) return [];

  const numParagraphs = checklist.numParagraphs;

  const numParagraphColumns =
    useCalculateNumParagraphColumns(checklist.sections ?? []) ?? numParagraphs;

  const overrideParagraphText: RevisionParagraph[] = [];

  const cellTextToParagraphs: CellTextParagraphs[] = [];

  for (const cell of wordChecklistCells) {
    const cellText = [];

    for (const paragraph of cell.paragraphs) {
      cellText.push(paragraph.text);

      overrideParagraphText.push(paragraph);
    }

    const cellTextString = cellText.join(" ");
    cellTextToParagraphs.push({
      text: cellTextString,
      paragraphs: cell.paragraphs,
    });
  }

  const header = [
    {
      text: "X",
      bold: true,
      alignment: "center",
    },
    {
      text: "Task",
      bold: true,
    },
    {
      text: "Score",
      bold: true,
      alignment: "center",
    },
  ];
  const rows = [] as any;

  for (const criteriaInstance of checklist.criteriaInstances) {
    const criteriaRows = usePDFChecklistCriteriaGenerator(
      criteriaInstance,
      allCriteria,
      numParagraphs,
      numParagraphColumns,
      cellTextToParagraphs,
      "#000000",
      hideScores,
      includeCriteriaCheckmarks,
      isStudentMode
    );

    rows.push(...criteriaRows);
  }

  if (checklist!.sections) {
    for (const section of checklist.sections) {
      if (
        section.paragraphPositionsData == undefined &&
        checklist.paragraphPositionsData
      ) {
        section.paragraphPositionsData = JSON.parse(
          JSON.stringify(checklist.paragraphPositionsData)
        );
      }

      usePDFChecklistSectionGenerator(
        section,
        rows,
        allCriteria,
        false,
        numParagraphs,
        numParagraphColumns,
        cellTextToParagraphs,
        false,
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode
      );
    }
  }

  for (const customCriteria of checklist.customCriteriaInstances ?? []) {
    const row = [
      useChecklistMarkCell(
        useShouldIncludeChecklistCriteriaCheckmark(
          customCriteria,
          includeCriteriaCheckmarks,
          isStudentMode
        )
      ),
      {
        text: customCriteria.labelOverride,
        color: "red",
        alignment: "right",
      },
      {
        text: (customCriteria.earnedPoints ?? 0).toString(),
        color: "red",
        alignment: "center",
      },
    ] as any;

    rows.push(row);
  }

  const showTotal =
    (submittedDocument.documentSubmissionType == undefined ||
      submittedDocument.documentSubmissionType ==
        DocumentSubmissionType.finalDraft) &&
    submittedDocument.allowFinalDraftGradedState != true;

  if (showTotal) {
    const footerRow = [
      {
        text: "",
      },
      {
        text: "Total",
        bold: true,
        alignment: "right",
      },
      {
        text: hideScores ? undefined : `${totalEarnedScore.toString()}/${totalScore}`,
        bold: true,
        alignment: "center",
      },
    ] as any;

    rows.push(footerRow);
  }

  if (showTotal != true && submittedDocument.documentGradedState != undefined) {
    const footerRow = [
      {
        text: DocumentGradedStateLabels[submittedDocument.documentGradedState],
        bold: true,
        alignment: "center",
        colSpan: 3,
        color:
          submittedDocument.documentGradedState ==
          DocumentGradedState.incomplete
            ? "#B2985C"
            : "#5A6E4F",
      },
    ] as any;

    rows.push(footerRow);
  }

  const unit = checklist.unit ? `Unit ${checklist.unit} ` : "";
  const suffix = checklist.isAdvancedChecklist ? " Advanced Aditions" : "";

  const label =
    checklist.levelName == "Practicum"
      ? "Practicum Checklist"
      : "Composition Checklist";

  const title = {
    text: `${unit}${label}${suffix}`,
    bold: true,
    fontSize: 18,
    lineHeight: 1.2,
  };

  const subtitlePrefix =
    checklist.levelName == "Practicum" ? "" : `${checklist.lessonName}: `;

  const subtitle = {
    text: `${subtitlePrefix}${checklist.subject ?? checklist.title}`,
    fontSize: 16,
    lineHeight: 1.2,
  };

  const data = [title, subtitle];

  if (
    checklist.levelName.includes("OSS") ||
    submittedDocument.enteredName != undefined ||
    submittedDocument.studentName != undefined
  ) {
    const enteredName = {
      text: `Name: ${
        submittedDocument.enteredName ?? submittedDocument.studentName
      }`,
      fontSize: 12,
      lineHeight: 1.2,
    };

    data.push(enteredName);
  }

  return [
    ...data,
    {
      text: "\n",
    },
    {
      table: {
        widths: [24, "*", "auto"],
        body: rows,
      },
      layout: {
        paddingLeft: (i: number) => (i === 0 ? 2 : 8),
        paddingRight: (i: number) => (i === 0 ? 2 : 8),
      },
    },
  ];
};
